import { Fragment } from 'react'
import { Pencil } from 'lucide-react'

import { PosOrderItemRow } from './pos-order-item-row'
import { PosMaterialRow } from './pos-material-row'
import type { PosOrderItem } from './pos-types'
import type { PackagingDraftItem } from '@/lib/packaging-draft'
import { rowKeyOf } from '@/lib/material-row-id'
import { formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    items: PosOrderItem[]
    orderNote: string
    /** Vật tư CÓ GIÁ gắn dòng đơn (line_key = product_id) — render inline dưới SP. */
    packagingItems?: PackagingDraftItem[]
    /** Tổng khách phải trả của đơn (đã chiết khấu, vật tư, VAT) — parent tính
     *  từ posOrderTotals để khớp panel thanh toán; không tính công nợ khách.
     *  Bỏ trống → hiện tổng tiền hàng như cũ. */
    payable?: { total: number; vatPercent: number; vatAmount: number }
    onUpdateItem: (
        index: number,
        field: 'quantity' | 'unit_price' | 'discount_percent',
        value: number,
    ) => void
    onToggleSplit?: (index: number) => void
    onRemoveItem: (index: number) => void
    onNoteChange: (note: string) => void
    onMaterialQtyChange?: (
        materialId: string,
        lineKey: string,
        value: string,
    ) => void
    onMaterialPriceChange?: (
        materialId: string,
        lineKey: string,
        value: string,
    ) => void
    onMaterialDiscountChange?: (
        materialId: string,
        lineKey: string,
        value: string,
    ) => void
    onMaterialRemove?: (materialId: string, lineKey: string) => void
}

export function PosOrderItems({
    items,
    orderNote,
    packagingItems = [],
    payable,
    onUpdateItem,
    onToggleSplit,
    onRemoveItem,
    onNoteChange,
    onMaterialQtyChange,
    onMaterialPriceChange,
    onMaterialDiscountChange,
    onMaterialRemove,
}: Props) {
    const noop = () => {}
    const totalAmount = items.reduce(
        (sum, item) =>
            sum +
            item.quantity * item.unit_price * (1 - item.discount_percent / 100),
        0,
    )
    const totalCount = items.reduce((sum, item) => sum + item.quantity, 0)

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Items list */}
            <div className="flex-1 overflow-auto">
                {/* Đơn chỉ vật tư (bán túi/hộp không kèm chè): vẫn phải hiện
                    dòng vật tư thêm tay thay vì báo "chưa có sản phẩm". */}
                {items.length === 0 && packagingItems.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-sm text-muted-foreground bg-muted/30">
                        {m.pos_order_empty()}
                    </div>
                ) : (
                    <div>
                        {/* Header */}
                        <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground border-b bg-muted/50 sticky top-0">
                            <span className="w-5">#</span>
                            <span className="w-4" />
                            <span className="flex-1">{m.order_products()}</span>
                            <span className="w-12 text-center">SL</span>
                            <span className="w-4" />
                            <span className="w-20 text-right">
                                {m.order_unit_price()}
                            </span>
                            <span className="w-14 text-center">CK%</span>
                            <span className="w-24 text-right">
                                {m.order_subtotal()}
                            </span>
                        </div>
                        {items.map((item, i) => {
                            const materialRows = packagingItems.filter(
                                (p) => p.line_key === item.product_id,
                            )
                            // Dòng GỘP (SP bật cờ, chưa tách) → vật tư giá hiệu lực 0.
                            const merged =
                                (item.price_includes_material ?? false) &&
                                !(item.material_price_split ?? false)
                            return (
                                <Fragment key={item.product_id}>
                                    <PosOrderItemRow
                                        item={item}
                                        index={i}
                                        onUpdate={onUpdateItem}
                                        onToggleSplit={onToggleSplit}
                                        onRemove={onRemoveItem}
                                    />
                                    {materialRows.map((row) => (
                                        <PosMaterialRow
                                            key={`mat-${item.product_id}-${row.packaging_material_id}`}
                                            row={row}
                                            merged={merged}
                                            onQtyChange={
                                                onMaterialQtyChange ?? noop
                                            }
                                            onPriceChange={
                                                onMaterialPriceChange ?? noop
                                            }
                                            onDiscountChange={
                                                onMaterialDiscountChange ?? noop
                                            }
                                            onRemove={onMaterialRemove ?? noop}
                                        />
                                    ))}
                                </Fragment>
                            )
                        })}
                        {/* Vật tư nhân viên tự thêm — ngang hàng dòng sản
                            phẩm, đồng nhất với màn tạo/sửa/chi tiết. */}
                        {packagingItems
                            .filter((p) => !p.line_key)
                            .map((row) => (
                                <PosMaterialRow
                                    key={rowKeyOf(row)}
                                    row={row}
                                    standalone
                                    merged={false}
                                    onQtyChange={onMaterialQtyChange ?? noop}
                                    onPriceChange={
                                        onMaterialPriceChange ?? noop
                                    }
                                    onDiscountChange={
                                        onMaterialDiscountChange ?? noop
                                    }
                                    onRemove={onMaterialRemove ?? noop}
                                />
                            ))}
                    </div>
                )}
            </div>

            {/* Bottom summary bar */}
            <div className="flex items-center gap-3 px-3 py-2 border-t bg-card shrink-0">
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    <Pencil className="size-3.5 text-muted-foreground shrink-0" />
                    <input
                        type="text"
                        placeholder={m.pos_order_note_placeholder()}
                        value={orderNote}
                        onChange={(e) => onNoteChange(e.target.value)}
                        className="flex-1 min-w-0 text-sm bg-transparent border-b border-dashed border-muted-foreground/30 focus:border-primary focus:outline-none py-0.5"
                    />
                </div>
                {payable ? (
                    <div
                        data-testid="pos-order-payable"
                        className="flex flex-col items-end shrink-0"
                    >
                        <div className="flex items-baseline gap-3">
                            <span className="text-sm text-muted-foreground">
                                {m.pos_order_payable_label({
                                    count: String(totalCount),
                                })}
                            </span>
                            <span className="text-lg font-bold text-primary">
                                {formatVND(payable.total)}
                            </span>
                        </div>
                        {payable.vatPercent > 0 && (
                            <div
                                data-testid="pos-order-vat"
                                className="flex items-baseline gap-1 text-xs text-muted-foreground"
                            >
                                <span>
                                    {m.pos_order_vat_included({
                                        percent: String(payable.vatPercent),
                                    })}
                                </span>
                                <span className="font-semibold text-foreground">
                                    {formatVND(payable.vatAmount)}
                                </span>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="text-sm text-muted-foreground shrink-0">
                            {m.pos_order_subtotal_label({
                                count: String(totalCount),
                            })}
                        </div>
                        <div className="text-lg font-bold text-primary shrink-0">
                            {formatVND(totalAmount)}
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
